import { Injectable } from '@angular/core';
import { getApp } from '@angular/fire/app';
import { doc, docData, DocumentReference, getFirestore } from '@angular/fire/firestore';
import { deleteObject, getStorage, ref, uploadBytesResumable, UploadTask } from '@angular/fire/storage';
import { FirebaseObservableService, FirebaseObservableServiceFactory, HttpService } from '@damofire/data-angular-commons';
import { DIVISIONS_COLLECTION, DOCUMENTS_COLLECTION, ID_FIELD_OPTIONS, Query, searchPath, SearchResult } from '@damofire/data-commons';
import { AuthDtoSignerService } from '@damofire/data-security';
import { OrganizationInfos } from '@damofire/data-stas';
import { Observable, Subscriber } from 'rxjs';
import { take } from 'rxjs/operators';
import { Document, DocumentMeta, DocumentType } from './storage.dto';

export function getDivisionDocumentDocRef(division: string, id: string): DocumentReference {
  return doc(getFirestore(), `${DIVISIONS_COLLECTION}/${division}/${DOCUMENTS_COLLECTION}/${id}`);
}

export function getDivisionDocument(division: string, id: string): Promise<Document> {
  return docData<Document>(getDivisionDocumentDocRef(division, id), ID_FIELD_OPTIONS).pipe(take(1)).toPromise();
}

export function listDivisionDocuments(id: string, query: Query, isAdmin = false): Promise<SearchResult<Document>> {
  if (!isAdmin) {
    query.filters['undisclosed'] = false;
  }
  return searchPath(getFirestore(), `${DIVISIONS_COLLECTION}/${id}/${DOCUMENTS_COLLECTION}`, query, ['filename']);
}

export async function getReportByVersionInspection(divisionId: string, version: string, inspection: string, isAdmin = false): Promise<SearchResult<Document>> {
  const results = await listDivisionDocuments(divisionId, { searchPhrase: 'Report', filters: { 'inspection._id': inspection, version } }, isAdmin);
  if (results.result.length > 0) {
    return results;
  }
  return listDivisionDocuments(
    divisionId,
    {
      searchPhrase: 'Rapport',
      filters: { 'inspection._id': inspection, version },
    },
    isAdmin,
  );
}

/**
 * Parses the standard ZIP/PDF filename format: [ORG] - [DIV] - [REPORT] - V[VERSION]
 * Returns the report type name and normalized version string.
 */
export function getReportFileAndVersion(filename: string): { version: string; report: string } {
  const parts = filename.split('-');
  if (parts.length < 4) {
    throw new Error(`Invalid filename format, expected at least 4 dash-separated parts: ${filename}`);
  }
  const report = parts[2].trim();
  let version = parts[3].trim().split('.')[0].toLowerCase();
  if (version.includes('(')) {
    version = version.split('(')[0].trim();
  }
  return { report, version };
}

export async function deleteStorageFile(storagePath: string, bucket: string): Promise<void> {
  const storage = getStorage(getApp(), bucket);
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (err: any) {
    if (err?.code !== 'storage/object-not-found') {
      throw err;
    }
  }
}

/**
 * Every document type is attached to an inspection: data/{org}/{div}/inspections/{id}
 */
function inspectionDocumentsPath(organizationInfos: OrganizationInfos): string {
  return `data/${organizationInfos.organization._id}/${organizationInfos.division._id}/inspections/${organizationInfos._id}`;
}

/**
 * Uploads a pre-extracted PDF artifact directly to its versioned storage path:
 * data/{org}/{div}/inspections/{id}/{version}/{filename}
 */
export function uploadDivisionDocumentPdf(
  blob: Blob,
  filename: string,
  version: string,
  type: DocumentType,
  organizationInfos: OrganizationInfos,
  bucket: string,
  meta?: DocumentMeta,
): Observable<number> {
  const storage = getStorage(getApp(), bucket);
  const task: UploadTask = uploadBytesResumable(ref(storage, `${inspectionDocumentsPath(organizationInfos)}/${version}/${filename}`), blob, {
    contentType: 'application/pdf',
    customMetadata: { ...meta, type },
  });

  return new Observable<number>((observer: Subscriber<number>) => {
    return task.on(
      'state_changed',
      (snapshot) => observer.next((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
      (err) => observer.error(err),
      () => {
        observer.next(100);
        observer.complete();
      },
    );
  });
}

export function uploadDivisionDocument(
  file: File,
  contentType: string,
  type: DocumentType,
  organizationInfos: OrganizationInfos,
  bucket: string,
  meta?: DocumentMeta,
): Observable<number> {
  const storage = getStorage(getApp(), bucket);
  const task: UploadTask = uploadBytesResumable(ref(storage, `${inspectionDocumentsPath(organizationInfos)}/${file.name}`), file, {
    contentType,
    customMetadata: { ...meta, type },
  });

  return new Observable<number>((observer: Subscriber<number>) => {
    return task.on(
      'state_changed',
      (snapshot) => {
        const percent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        observer.next(percent);
      },
      (err) => observer.error(err),
      () => {
        observer.next(100);
        observer.complete();
      },
    );
  });
}

@Injectable()
export class DocumentStorageService {
  private readonly service: FirebaseObservableService<Document>;

  constructor(
    private readonly http: HttpService,
    private readonly dtoSigner: AuthDtoSignerService,
    private readonly firebaseObservableServiceFactory: FirebaseObservableServiceFactory,
  ) {
    this.service = firebaseObservableServiceFactory.createService(DOCUMENTS_COLLECTION);
  }

  async setUndisclosed(document: Document, undisclosed: boolean): Promise<void> {
    const list = await listDivisionDocuments(document.division._id, { filters: { version: document.version, 'inspection._id': document.inspection._id } }, true);
    for (const subDoc of list.result) {
      await this.dtoSigner.update({ undisclosed }, `${DIVISIONS_COLLECTION}/${subDoc.division._id}/${DOCUMENTS_COLLECTION}/${subDoc._id}`);
    }
  }

  observeDivisionDocuments(id: string, q: Query, isAdmin = false): Observable<SearchResult<Document>> {
    if (!isAdmin) {
      q.filters['undisclosed'] = false;
    }
    return this.service.observeCollectionSearch(q, ['filename'], [], `${DIVISIONS_COLLECTION}/${id}/${DOCUMENTS_COLLECTION}`);
  }
}
